import {
  Box,
  Chip,
  ContentTemplate,
  CustomAutocomplete,
  InputLabel,
  SingleSelectionRadio,
  Typography,
} from 'web-library';
import {Checkbox, FormControlLabel, Radio, RadioGroup} from '@mui/material';
import React, { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ListboxComponent from 'web-library/stories/molecule/CustomAutocomplete/ListboxComponent';
import RecipientListDataTable from './RecipientListDataTable';
import { RecipientTypeMode, SelectRecipientByMode } from '../index';
import { ConfigurationType } from '../../../../api/candidate/config/model';
import { NotificationSearchRecipientResponseContentObject } from '../../../../api-refactored/manage-notifications/usePostNotifcationSearchRecipient';

const displayCandidateTypeError = (candidateTypeValue: string[], displayError: boolean) =>
  candidateTypeValue.length === 0 && displayError;

interface RecipientContentProps {
  classes: Record<string,string>;
  featureValue: string;
  schoolValue: string[];
  displayError: boolean;
  examLevelValue: string[];
  candidateTypeValue: string[];
  recipientTypeMode: RecipientTypeMode;
  selectRecipientByMode: SelectRecipientByMode;
  setRecipientTypeMode: React.Dispatch<React.SetStateAction<RecipientTypeMode>>;
  setSelectRecipientByMode: React.Dispatch<React.SetStateAction<SelectRecipientByMode>>;
  setSchoolValue: React.Dispatch<React.SetStateAction<string[]>>;
  setExamLevelValue: React.Dispatch<React.SetStateAction<string[]>>;
  setCandidateTypeValue: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedCandidates: React.Dispatch<
    React.SetStateAction<NotificationSearchRecipientResponseContentObject[]>
  >;
  notificationSchools?: ConfigurationType[];
  notificationExamLevels?: ConfigurationType[];
  notificationCandidateTypes?: ConfigurationType[];
  selectedCandidates: NotificationSearchRecipientResponseContentObject[];
}

const RecipientContent = ({
  classes,
  selectRecipientByMode,
  recipientTypeMode,
  setRecipientTypeMode,
  setSelectRecipientByMode,
  notificationCandidateTypes,
  notificationExamLevels,
  examLevelValue,
  displayError,
  setExamLevelValue,
  candidateTypeValue,
  setCandidateTypeValue,
  notificationSchools,
  schoolValue,
  setSchoolValue,
  featureValue,
  selectedCandidates,
  setSelectedCandidates,
}: RecipientContentProps) => {
  const { t } = useTranslation();
  const examLevelDefaultObject = useMemo(() => {
    const examLevelArr: ConfigurationType[] = [];
    examLevelValue?.forEach((selectedExamLevel) => {
      const individualSelectedExamLevel = notificationExamLevels?.find(
        (examLevel: ConfigurationType) =>
            examLevel.code === selectedExamLevel
      );
      examLevelArr.push(individualSelectedExamLevel as ConfigurationType);
    });
    return examLevelArr;
  }, [notificationExamLevels, examLevelValue]);

  const candidateTypeDefaultObject = useMemo(() => {
    const candidateTypeArr: ConfigurationType[] = [];
    candidateTypeValue?.forEach((selectedCandidateType) => {
      const individualSelectedCandidateType = notificationCandidateTypes?.find(
        (candidateType: ConfigurationType) =>
            candidateType.code === selectedCandidateType
      );
      candidateTypeArr.push(individualSelectedCandidateType as ConfigurationType);
    });
    return candidateTypeArr;
  }, [notificationCandidateTypes, candidateTypeValue]);

  const schoolPrivateCentreDefaultObject = useMemo(() => {
    const schoolPrivateCentreArr: ConfigurationType[] = [];
    schoolValue?.forEach((selectedSchoolPrivateCentre) => {
      const individualSelectedAppointment = notificationSchools?.find(
        (schoolPrivateCentre: ConfigurationType) =>
          schoolPrivateCentre.code === selectedSchoolPrivateCentre
      );
      schoolPrivateCentreArr.push(individualSelectedAppointment as ConfigurationType);
    });
    return schoolPrivateCentreArr;
  }, [notificationSchools, schoolValue]);

  const onChangeCustomAutocomplete = (
    _event: ChangeEvent<{}>,
    value: ConfigurationType[],
    setValue: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const valueArr = value.map((individualValue) => individualValue.code);
    setValue(valueArr);
  };

  return (
    <ContentTemplate
      isContinuable
      continuous
      isDividerBottom
      type={selectRecipientByMode === SelectRecipientByMode.Individual ? 'full' : 'default'}
    >
      <Box className={classes.recipientTypeContainer}>
        <Typography className={classes.sectionTitle} variant="h3">
          {t('announcementsNotifications->>CreateNewAnnouncement.Recipients')}
        </Typography>
        <Box className={classes.radioContainer}>
          <InputLabel className={classes.keyLabel}>
            {t('announcementsNotifications->>SendNotification.RecipientType')}
            <Typography className={classes.requiredLabel}>*</Typography>
          </InputLabel>
          <SingleSelectionRadio
            data={[
              {
                label: t('announcementsNotifications->>SendNotification.Candidate'),
                value: RecipientTypeMode.Candidate,
              },
              {
                label: t('announcementsNotifications->>SendNotification.Coordinator'),
                value: RecipientTypeMode.Coordinator,
              },
            ]}
            onChange={(event) => setRecipientTypeMode(event.target.value as RecipientTypeMode)}
            value={recipientTypeMode}
          />
        </Box>
        <Box className={classes.radioContainer}>
          <InputLabel className={classes.keyLabel}>
            {t('announcementsNotifications->>SendNotification.SelectRecipientsBy')}
            <Typography className={classes.requiredLabel}>*</Typography>
          </InputLabel>
          <RadioGroup
            value={selectRecipientByMode}
            onChange={(event) =>
              setSelectRecipientByMode(event.target.value as SelectRecipientByMode)
            }
          >
            <Box className={classes.selectedRadioButton}>
              <FormControlLabel
                key="form-control-label-select-recipient-by-mode-usergroup"
                value={SelectRecipientByMode.UserGroup}
                control={
                  <Radio
                    color="primary"
                    checked={selectRecipientByMode === SelectRecipientByMode.UserGroup}
                  />
                }
                label={t('announcementsNotifications->>SendNotification.UserGroup')}
              />
            </Box>
            <Box
              className={
                recipientTypeMode === RecipientTypeMode.Coordinator
                  ? classes.disabledRadioButton
                  : ''
              }
            >
              <FormControlLabel
                key="form-control-label-select-recipient-by-mode-individual"
                value={SelectRecipientByMode.Individual}
                control={
                  <Radio
                    color="primary"
                    classes={{
                      root:
                        recipientTypeMode === RecipientTypeMode.Coordinator
                          ? classes.disabledRadioButton
                          : '',
                    }}
                    checked={selectRecipientByMode === SelectRecipientByMode.Individual}
                  />
                }
                label={t('announcementsNotifications->>SendNotification.Individual')}
              />
            </Box>
          </RadioGroup>
        </Box>
        {notificationCandidateTypes &&
          notificationExamLevels &&
          examLevelValue &&
          candidateTypeValue &&
          recipientTypeMode === RecipientTypeMode.Candidate &&
          selectRecipientByMode === SelectRecipientByMode.UserGroup && (
            <>
              <Box className={classes.dropDownContainer}>
                <CustomAutocomplete<ConfigurationType, true, false, undefined>
                    id="inputSelect-announcementExamLevel"
                    label={t('announcementsNotifications->>SendNotification.ExamLevel')}
                    isRequired
                    value={examLevelDefaultObject}
                    error={examLevelValue.length === 0 && displayError}
                    helperText={
                      examLevelValue.length === 0 && displayError
                          ? t('announcementsNotifications->>SendNotification.NoExamLevelErrorMessage')
                          : ''
                    }
                    options={notificationExamLevels}
                    getOptionLabel={(option) => (option.description)}
                    multiple
                    disableCloseOnSelect
                    onChange={(_event, value) => {
                      onChangeCustomAutocomplete(_event, value as ConfigurationType[], setExamLevelValue);
                    }}
                    renderOption={(props, option, state, __) =>
                        <>
                          <Checkbox checked={state.selected} id={`autocomplete-option-${option.description}`} />
                          {option.description}
                        </>
                    }
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, tagIndex) => (
                            <Chip
                                id="auto-complete"
                                label={option.description}
                                {...getTagProps({ index: tagIndex })}
                                key={`autocomplete-exam-level-key-${option.description}`}
                            />
                        ))
                    }
                />
              </Box>
              <Box className={classes.dropDownContainer}>
                <CustomAutocomplete<ConfigurationType, true, false, undefined>
                    id="inputSelect-announcementCandidateType"
                    label={t('announcementsNotifications->>SendNotification.CandidateType')}
                    isRequired
                    value={candidateTypeDefaultObject}
                    helperText={
                      displayCandidateTypeError(candidateTypeValue, displayError)
                          ? t(
                              'announcementsNotifications->>SendNotification.NoCandidateTypeErrorMessage'
                          )
                          : ''
                    }
                    error={displayCandidateTypeError(candidateTypeValue, displayError)}
                    options={notificationCandidateTypes}
                    getOptionLabel={(option) => (option.description)}
                    multiple
                    disableCloseOnSelect
                    onChange={(_event, value) => {
                      onChangeCustomAutocomplete(_event, value as ConfigurationType[], setCandidateTypeValue);
                    }}
                    renderOption={(props, option, state, __) =>
                        <>
                          <Checkbox checked={state.selected} id={`autocomplete-option-${option.description}`} />
                          {option.description}
                        </>
                    }
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, tagIndex) => (
                            <Chip
                                id="auto-complete"
                                label={option.description}
                                {...getTagProps({ index: tagIndex })}
                                key={`autocomplete-candidate-type-key-${option.description}`}
                            />
                        ))
                    }
                />
              </Box>
            </>
          )}
        {selectRecipientByMode === SelectRecipientByMode.UserGroup &&
          notificationSchools &&
          schoolValue && (
            <Box className={classes.lastDropDownContainer}>
              <CustomAutocomplete
                id="inputSelect-notificationSchools"
                label={t('announcementsNotifications->>SendNotification.SchoolPrivateCentre')}
                placeholder={t(
                  'announcementsNotifications->>SendNotification.SelectSchoolPrivateCentre'
                )}
                isRequired
                error={schoolPrivateCentreDefaultObject.length === 0 && displayError}
                helperText={
                  schoolPrivateCentreDefaultObject.length === 0 && displayError
                    ? t(
                        'announcementsNotifications->>SendNotification.NoSchoolPrivateCentreErrorMessage'
                      )
                    : ''
                }
                options={notificationSchools}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
                multiple
                disableCloseOnSelect
                value={schoolPrivateCentreDefaultObject}
                onChange={(_event, value) => {
                  onChangeCustomAutocomplete(_event, value as ConfigurationType[], setSchoolValue);
                }}
                renderOption={(props, option, state, __) =>
                    <>
                      <Checkbox checked={state.selected} id={`autocomplete-option-${option.description}`} />
                      {option.description}
                    </>
                }
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, tagIndex) => (
                    <Chip
                      id="auto-complete"
                      label={option.description}
                      {...getTagProps({ index: tagIndex })}
                      key={`autocomplete-school-key-${option.description}`}
                    />
                  ))
                }
                slotProps={{
                  listbox: {
                    component: ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>
                  }
                }
                }
              />
            </Box>
          )}
      </Box>
      {recipientTypeMode === RecipientTypeMode.Candidate &&
        selectRecipientByMode === SelectRecipientByMode.Individual && (
          <Box className={classes.recipientListContainer}>
            <RecipientListDataTable
              featureValue={featureValue}
              selectedCandidates={selectedCandidates}
              setSelectedCandidates={setSelectedCandidates}
            />
          </Box>
        )}
    </ContentTemplate>
  );
};

export default RecipientContent;
